import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { INVOICE_STATUS_DEFS, INVOICE_STATUS_MAP, type InvoiceStatus } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ExportMenu } from '../../components/ExportMenu';
import {
  exportDay,
  exportNotes,
  exportNum,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface InvoiceRow {
  id: string;
  number: string;
  status: InvoiceStatus;
  title: string | null;
  total: string;
  paidAmount: string;
  issuedAt: string;
  dueAt: string;
  account: { id: string; name: string } | null;
}

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في invoices.service.ts. */
const INVOICE_FILTERS: FilterDef[] = [
  { key: 'number', label: 'الرقم', kind: 'text', sortable: true },
  { key: 'account', label: 'الحساب', kind: 'text' },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: INVOICE_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  { key: 'total', label: 'الإجمالي', kind: 'number', sortable: true },
  { key: 'paidAmount', label: 'المحصّل', kind: 'number' },
  { key: 'dueAt', label: 'الاستحقاق', kind: 'date', sortable: true },
];

interface InvoicePage {
  items: InvoiceRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** مجاميع كل ما يطابق الشروط (المدى والفلاتر) — لا الصفحة وحدها. */
  sums: { total: number; paid: number; outstanding: number };
}

/**
 * «من تاريخ / إلى تاريخ» على تاريخ الإصدار.
 *
 * طرفٌ وحده يحدّ — «من أوّل الشهر» بلا نهاية سؤالٌ معقول — واليوم الأخير
 * داخلٌ كلّه، والخادم يحسبه كذلك. والقيم نصوص سنة-شهر-يوم كما في الرابط.
 */
function DateRange({
  from,
  to,
  onChange,
}: {
  from?: string;
  to?: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
}) {
  return (
    <span className="row" style={{ gap: 8 }}>
      <input
        className="input"
        type="date"
        value={from ?? ''}
        max={to || undefined}
        aria-label="من تاريخ"
        title="من تاريخ"
        onChange={(e) => onChange(e.target.value || undefined, to)}
      />
      <input
        className="input"
        type="date"
        value={to ?? ''}
        min={from || undefined}
        aria-label="إلى تاريخ"
        title="إلى تاريخ"
        onChange={(e) => onChange(from, e.target.value || undefined)}
      />
      {(from || to) && (
        <button
          type="button"
          className="btn btn-sm btn-icon btn-ghost"
          aria-label="مسح مدى التاريخ"
          title="مسح مدى التاريخ"
          onClick={() => onChange(undefined, undefined)}
        >
          <Icon name="x" size={14} />
        </button>
      )}
    </span>
  );
}

export function InvoicesList() {
  const navigate = useNavigate();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);

  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['invoices', query],
    queryFn: () => api<InvoicePage>(`/finance/invoices${qs(query)}`),
  });

  const aging = useQuery({
    queryKey: ['aging'],
    queryFn: () => api<{ totalOutstanding: number }>('/finance/payments/aging'),
  });

  /*
   * المستحق على السجلّ كلّه من تقادم الذمم؛ فإن ضُيّق المدى أو فُلتر عمودٌ
   * فمن مجاميع القائمة نفسها، وإلا قال الرقم شيئًا والجدول تحته شيئًا آخر.
   */
  const narrowed = Boolean(state.from || state.to || state.f);
  const outstanding = narrowed
    ? (list.data?.sums.outstanding ?? 0)
    : (aging.data?.totalOutstanding ?? 0);
  const now = Date.now();

  /*
   * التصدير: كل الفواتير المطابقة للبحث والمدى والحالة والفلاتر، لا الصفحة
   * المعروضة وحدها. والإجمالي بلا الملغاة كبطاقات الصفحة.
   */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<InvoiceRow>('/finance/invoices', query, 100);
    const rows = items.map((inv) => ({
      number: inv.number,
      account: inv.account?.name ?? null,
      status: INVOICE_STATUS_MAP[inv.status]?.label ?? inv.status,
      issuedAt: exportDay(inv.issuedAt),
      total: exportNum(inv.total),
      paidAmount: exportNum(inv.paidAmount),
      remaining: Number(inv.total) - Number(inv.paidAmount),
      dueAt: exportDay(inv.dueAt),
    }));
    const live = items.filter((inv) => inv.status !== 'CANCELLED');
    const sum = (pick: (inv: InvoiceRow) => number) => live.reduce((s, inv) => s + pick(inv), 0);
    return {
      title: 'الفواتير',
      sheets: [
        {
          title: 'الفواتير',
          period: exportPeriod(state.from, state.to),
          sections: [
            {
              columns: [
                { key: 'number', label: 'الرقم', kind: 'code' },
                { key: 'account', label: 'الحساب', minWidth: 16 },
                { key: 'status', label: 'الحالة', wrap: false },
                { key: 'issuedAt', label: 'الإصدار', kind: 'date' },
                { key: 'total', label: 'الإجمالي', kind: 'money' },
                { key: 'paidAmount', label: 'المحصّل', kind: 'money' },
                { key: 'remaining', label: 'المتبقي', kind: 'money' },
                { key: 'dueAt', label: 'الاستحقاق', kind: 'date' },
              ],
              rows,
              totals: {
                total: sum((inv) => Number(inv.total)),
                paidAmount: sum((inv) => Number(inv.paidAmount)),
                remaining: sum((inv) => Number(inv.total) - Number(inv.paidAmount)),
              },
              totalsLabel: live.length < items.length ? 'الإجمالي (بلا الملغاة)' : 'الإجمالي',
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: [
              state.unpaidOnly && 'غير المسدَّدة فقط',
              state.status &&
                `الحالة: ${INVOICE_STATUS_MAP[state.status as InvoiceStatus]?.label ?? state.status}`,
              ...describeFilters(INVOICE_FILTERS, filters),
            ],
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="الفواتير"
        subtitle={list.data ? plural(list.data.total, WORDS.invoice) : undefined}
        actions={
          <>
            <Link to="/finance/aging" className="btn">
              <Icon name="chart" size={15} />
              تقادم الذمم
            </Link>
            <button className="btn btn-primary" onClick={() => navigate('/finance/invoices/new')}>
              <Icon name="plus" size={15} />
              فاتورة جديدة
            </button>
          </>
        }
      />

      <div className="stats">
        <Stat
          label="إجمالي المستحق"
          value={
            <span
              className="num"
              style={{ color: outstanding > 0 ? 'var(--danger)' : undefined }}
            >
              {money(outstanding)}
            </span>
          }
          note={narrowed ? 'ضمن المدى والفلاتر المختارة' : 'على كل الفواتير غير المسدَّدة'}
        />
        <Stat
          label="المفوتر"
          value={<span className="num">{money(list.data?.sums.total ?? 0)}</span>}
          note="ما يطابق الشروط، بلا الملغاة"
        />
        <Stat
          label="المحصّل"
          value={<span className="num">{money(list.data?.sums.paid ?? 0)}</span>}
          note="ما يطابق الشروط، بلا الملغاة"
        />
      </div>

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث برقم الفاتورة"
        />

        <DateRange
          from={state.from}
          to={state.to}
          onChange={(from, to) => patch({ from, to })}
        />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        <div className="row" style={{ gap: 2 }}>
          <button
            className={`btn btn-sm${!state.status && !state.unpaidOnly ? ' btn-primary' : ''}`}
            onClick={() => patch({ status: undefined, unpaidOnly: undefined })}
          >
            الكل
          </button>
          <button
            className={`btn btn-sm${state.unpaidOnly ? ' btn-primary' : ''}`}
            onClick={() => patch({ unpaidOnly: state.unpaidOnly ? undefined : 'true', status: undefined })}
          >
            غير المسدَّدة
          </button>
          {INVOICE_STATUS_DEFS.map((s) => (
            <button
              key={s.key}
              className={`btn btn-sm${state.status === s.key ? ' btn-primary' : ''}`}
              onClick={() => patch({ status: s.key, unpaidOnly: undefined })}
            >
              {s.label}
            </button>
          ))}
        </div>

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={INVOICE_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={INVOICE_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={INVOICE_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/invoices/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الرقم
                  <HeaderFilter colKey="number" />
                </th>
                <th>
                  الحساب
                  <HeaderFilter colKey="account" />
                </th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
                <th>
                  الإجمالي
                  <HeaderFilter colKey="total" />
                </th>
                <th>
                  المحصّل
                  <HeaderFilter colKey="paidAmount" />
                </th>
                <th>المتبقي</th>
                <th>
                  الاستحقاق
                  <HeaderFilter colKey="dueAt" />
                </th>
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={7} />}

            {list.data && (
              <tbody>
                {list.data.items.map((inv) => {
                  const remaining = Number(inv.total) - Number(inv.paidAmount);
                  const late =
                    remaining > 0 &&
                    new Date(inv.dueAt).getTime() < now &&
                    inv.status !== 'CANCELLED' &&
                    inv.status !== 'DRAFT';
                  return (
                    <tr
                      key={inv.id}
                      className="clickable"
                      onClick={() => navigate(`/finance/invoices/${inv.id}`)}
                    >
                      <td className="cell-strong num">{inv.number}</td>
                      <td>
                        {inv.account ? (
                          <Link
                            to={`/crm/accounts/${inv.account.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {inv.account.name}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <Badge kind={INVOICE_STATUS_MAP[inv.status]?.kind}>
                          {INVOICE_STATUS_MAP[inv.status]?.label}
                        </Badge>
                      </td>
                      <td className="num">{money(inv.total)}</td>
                      <td className="num cell-mute">{money(inv.paidAmount)}</td>
                      <td className="num">
                        <span style={{ color: remaining > 0 ? 'var(--danger)' : undefined }}>
                          {money(remaining)}
                        </span>
                      </td>
                      <td className={late ? 'date' : 'cell-mute date'}>
                        <span style={{ color: late ? 'var(--danger)' : undefined }}>
                          {date(inv.dueAt)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد فواتير"
            text="تُصدر الفاتورة عن عرض سعر مقبول، أو تُنشأ مباشرة."
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>
    </div>
  );
}
